import { useEffect, useState, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  Edit as EditIcon,
  LockReset as LockResetIcon,
  Refresh as RefreshIcon,
  VpnKey as VpnKeyIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { adminApi, type AdminTenant, type AdminTenantSearchField, type SoftwareVendor } from '@/api/admin';
import type { TenantSettings } from '@/types';
import { INDUSTRY_OPTIONS, TENANT_SEARCH_FIELD_OPTIONS } from '@/constants/options';
import { brand } from '@/theme/theme';
import { PageHeader, PaginationFooter } from '@/components';
import { AdminSearchWithFieldSelect } from '@/components/AdminSearchWithFieldSelect';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function industryLabel(value?: string | null): string {
  if (!value) return '—';
  return INDUSTRY_OPTIONS.find(o => o.value === value)?.label ?? value;
}

function swVendorLabel(softwareVendorId: number | undefined | null, vendors: SoftwareVendor[]): string {
  if (!softwareVendorId) return '—';
  const v = vendors.find(v => v.id === softwareVendorId);
  return v ? `${v.companyName} (${v.softwareName})` : '—';
}

function TenantTypeChip({ type }: { type?: string | null }) {
  if (!type) return null;
  const palette: Record<string, { bg: string; fg: string }> = {
    RETAILER:       { bg: '#C17BAF', fg: '#FFFFFF' },
    WHOLESALER:     { bg: '#7B61FF', fg: '#ffffff' },
    REPRESENTATIVE: { bg: '#3DD68C', fg: '#0E3520' },
    OTHERS:         { bg: '#9CA3AF', fg: '#1F2937' },
  };
  const c = palette[type] ?? palette.OTHERS;
  return (
    <Chip
      size="small"
      label={type === 'WHOLESALER' ? 'SUPPLIER' : type}
      sx={{
        height: 18, fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
        background: c.bg, color: c.fg, borderRadius: 0.5,
        '& .MuiChip-label': { px: 0.75 },
      }}
    />
  );
}

function StatusChip({ status }: { status?: string | null }) {
  if (!status) return null;
  const palette: Record<string, { bg: string; fg: string }> = {
    TRIAL:     { bg: '#dbeafe', fg: '#1e40af' },
    ACTIVE:    { bg: '#dcfce7', fg: '#166534' },
    SUSPENDED: { bg: '#fee2e2', fg: '#991b1b' },
    EXPIRED:   { bg: '#fef3c7', fg: '#92400e' },
  };
  const c = palette[status] ?? { bg: '#f3f4f6', fg: '#374151' };
  return (
    <Chip size="small" label={status}
      sx={{ height: 18, fontSize: 10, fontWeight: 700, background: c.bg, color: c.fg,
            borderRadius: 0.5, '& .MuiChip-label': { px: 0.75 } }} />
  );
}

// ---------------------------------------------------------------------------
// Sorting helpers
// ---------------------------------------------------------------------------

type SortCol = 'name' | 'tenantType' | 'industry' | 'phone' | 'status' | 'city';
type SortDir = 'asc' | 'desc';

function sortedTenants(rows: AdminTenant[], col: SortCol, dir: SortDir): AdminTenant[] {
  return [...rows].sort((a, b) => {
    let av = '';
    let bv = '';
    switch (col) {
      case 'name':       av = a.name ?? '';       bv = b.name ?? '';       break;
      case 'tenantType': av = a.tenantType ?? '';  bv = b.tenantType ?? ''; break;
      case 'industry':   av = a.industry ?? '';    bv = b.industry ?? '';   break;
      case 'phone':      av = a.phone ?? '';       bv = b.phone ?? '';      break;
      case 'status':     av = a.status ?? '';      bv = b.status ?? '';     break;
      case 'city':       av = a.city ?? '';        bv = b.city ?? '';       break;
    }
    const cmp = av.localeCompare(bv, undefined, { sensitivity: 'base' });
    return dir === 'asc' ? cmp : -cmp;
  });
}

// ---------------------------------------------------------------------------
// Change Password Dialog
// ---------------------------------------------------------------------------

function ChangePasswordDialog({
  tenant,
  onClose,
}: {
  tenant: AdminTenant | null;
  onClose: () => void;
}) {
  const [newPassword,     setNewPassword]     = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew,         setShowNew]         = useState(false);
  const [showConfirm,     setShowConfirm]     = useState(false);
  const [saving,          setSaving]          = useState(false);
  const [error,           setError]           = useState('');
  const [success,         setSuccess]         = useState(false);

  // Reset state whenever a new tenant is selected
  useEffect(() => {
    setNewPassword('');
    setConfirmPassword('');
    setShowNew(false);
    setShowConfirm(false);
    setError('');
    setSuccess(false);
    setSaving(false);
  }, [tenant]);

  if (!tenant) return null;

  const passwordStrength = (pwd: string): { label: string; color: string } => {
    if (pwd.length === 0) return { label: '', color: 'transparent' };
    if (pwd.length < 6)   return { label: 'Too short', color: '#ef4444' };
    if (pwd.length < 8)   return { label: 'Weak', color: '#f59e0b' };
    const hasUpper = /[A-Z]/.test(pwd);
    const hasNum   = /[0-9]/.test(pwd);
    const hasSpec  = /[^A-Za-z0-9]/.test(pwd);
    const score = (hasUpper ? 1 : 0) + (hasNum ? 1 : 0) + (hasSpec ? 1 : 0);
    if (score >= 2) return { label: 'Strong',  color: '#16a34a' };
    return               { label: 'Medium',   color: '#2563eb' };
  };

  const strength = passwordStrength(newPassword);
  const mismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;

  async function handleSave() {
    setError('');
    if (!tenant) return;
    if (!newPassword) { setError('Password cannot be empty.'); return; }
    if (newPassword.length < 6) { setError('Password must be at least 6 characters.'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match.'); return; }

    setSaving(true);
    try {
      await adminApi.changePassword(tenant.id, newPassword);
      setSuccess(true);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Failed to change password.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={!!tenant} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, fontSize: 15, pb: 0.5, pr: 6 }}>
        Change Password
        <IconButton size="small" onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: '12px !important' }}>
        {success ? (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography sx={{ fontSize: 36, mb: 1 }}>✅</Typography>
            <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#16a34a', mb: 0.5 }}>
              Password Changed
            </Typography>
            <Typography sx={{ fontSize: 13, color: brand.inkSoft }}>
              Password for <strong>{tenant.name}</strong> has been updated successfully.
            </Typography>
            <Button variant="contained" sx={{ mt: 2 }} onClick={onClose}>
              Done
            </Button>
          </Box>
        ) : (
          <Stack spacing={2}>
            {/* Tenant info pill */}
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              bgcolor: '#f8fafc', borderRadius: 1, px: 1.5, py: 1,
              border: '1px solid', borderColor: 'divider',
            }}>
              <TenantTypeChip type={tenant.tenantType} />
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: brand.navy, lineHeight: 1.2 }}>
                  {tenant.name}
                </Typography>
                <Typography sx={{ fontSize: 11, color: brand.inkSoft }}>
                  {tenant.phone ?? tenant.email ?? tenant.code}
                </Typography>
              </Box>
            </Box>

            {/* New password */}
            <TextField
              label="New Password"
              type={showNew ? 'text' : 'password'}
              value={newPassword}
              onChange={e => { setNewPassword(e.target.value); setError(''); }}
              fullWidth size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowNew(v => !v)} edge="end">
                      {showNew ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Password strength bar */}
            {newPassword.length > 0 && (
              <Box sx={{ mt: -1 }}>
                <Box sx={{
                  height: 4, borderRadius: 2, bgcolor: '#e5e7eb', overflow: 'hidden',
                }}>
                  <Box sx={{
                    height: '100%', borderRadius: 2,
                    bgcolor: strength.color,
                    width: strength.label === 'Too short' ? '20%'
                          : strength.label === 'Weak'     ? '40%'
                          : strength.label === 'Medium'   ? '70%'
                          : '100%',
                    transition: 'width 0.2s, background-color 0.2s',
                  }} />
                </Box>
                {strength.label && (
                  <Typography sx={{ fontSize: 11, color: strength.color, mt: 0.25 }}>
                    {strength.label}
                  </Typography>
                )}
              </Box>
            )}

            {/* Confirm password */}
            <TextField
              label="Confirm Password"
              type={showConfirm ? 'text' : 'password'}
              value={confirmPassword}
              onChange={e => { setConfirmPassword(e.target.value); setError(''); }}
              fullWidth size="small"
              error={mismatch}
              helperText={mismatch ? 'Passwords do not match' : ''}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowConfirm(v => !v)} edge="end">
                      {showConfirm ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {error && (
              <Alert severity="error" sx={{ py: 0.5, fontSize: 13 }}>{error}</Alert>
            )}

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', pt: 0.5 }}>
              <Button variant="outlined" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <LockResetIcon fontSize="small" />}
                onClick={handleSave}
                disabled={saving || !newPassword || !confirmPassword || mismatch}
                sx={{ bgcolor: brand.navy, '&:hover': { bgcolor: '#1e3460' } }}
              >
                {saving ? 'Saving…' : 'Set Password'}
              </Button>
            </Box>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// View API Key Dialog
// ---------------------------------------------------------------------------

function ViewApiKeyDialog({
  tenant,
  onClose,
}: {
  tenant: AdminTenant | null;
  onClose: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [apiKey,  setApiKey]  = useState('');
  const [showKey, setShowKey] = useState(false);
  const [copied,  setCopied]  = useState(false);

  useEffect(() => {
    if (!tenant) return;
    setLoading(true);
    setError('');
    setApiKey('');
    setShowKey(false);
    setCopied(false);
    adminApi.getTenantApiKey(tenant.id)
      .then(res => setApiKey(res.apiKey))
      .catch((err: any) => setError(err?.response?.data?.message ?? err?.message ?? 'Failed to load API key.'))
      .finally(() => setLoading(false));
  }, [tenant]);

  if (!tenant) return null;

  function handleCopy() {
    navigator.clipboard.writeText(apiKey).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  const maskedKey = apiKey ? apiKey.slice(0, 8) + '•'.repeat(Math.max(0, apiKey.length - 8)) : '';

  return (
    <Dialog open={!!tenant} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, fontSize: 15, pb: 0.5, pr: 6 }}>
        View API Key
        <IconButton size="small" onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: '12px !important' }}>
        <Stack spacing={2}>
          {/* Tenant pill */}
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            bgcolor: '#f8fafc', borderRadius: 1, px: 1.5, py: 1,
            border: '1px solid', borderColor: 'divider',
          }}>
            <TenantTypeChip type={tenant.tenantType} />
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: brand.navy, lineHeight: 1.2 }}>
                {tenant.name}
              </Typography>
              <Typography sx={{ fontSize: 11, color: brand.inkSoft }}>
                {tenant.phone ?? tenant.email ?? tenant.code}
              </Typography>
            </Box>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={22} />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ py: 0.5, fontSize: 13 }}>{error}</Alert>
          ) : (
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              bgcolor: '#f1f5f9', border: '1px solid', borderColor: 'divider',
              borderRadius: 1, px: 1.5, py: 1,
            }}>
              <Typography sx={{ fontSize: 12, fontFamily: 'monospace', flex: 1, wordBreak: 'break-all' }}>
                {showKey ? apiKey : maskedKey}
              </Typography>
              <Tooltip title={showKey ? 'Hide' : 'Show'}>
                <IconButton size="small" onClick={() => setShowKey(v => !v)}>
                  {showKey ? <VisibilityOffIcon sx={{ fontSize: 16 }} /> : <VisibilityIcon sx={{ fontSize: 16 }} />}
                </IconButton>
              </Tooltip>
              <Tooltip title={copied ? 'Copied!' : 'Copy'}>
                <IconButton size="small" onClick={handleCopy} sx={{ color: copied ? '#16a34a' : 'inherit' }}>
                  <ContentCopyIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="outlined" onClick={onClose}>Close</Button>
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Regenerate API Key Dialog
// ---------------------------------------------------------------------------

function RegenerateApiKeyDialog({
  tenant,
  onClose,
}: {
  tenant: AdminTenant | null;
  onClose: () => void;
}) {
  const [password,   setPassword]   = useState('');
  const [showPwd,    setShowPwd]    = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState('');
  const [newApiKey,  setNewApiKey]  = useState('');
  const [copied,     setCopied]     = useState(false);

  useEffect(() => {
    setPassword('');
    setShowPwd(false);
    setError('');
    setNewApiKey('');
    setCopied(false);
    setSaving(false);
  }, [tenant]);

  if (!tenant) return null;

  async function handleRegenerate() {
    setError('');
    if (!password) { setError('Enter your password to confirm.'); return; }
    setSaving(true);
    try {
      const result = await adminApi.regenerateTenantApiKey(tenant!.id, password);
      setNewApiKey(result.apiKey);
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? err?.message ?? '';
      setError(msg.toLowerCase().includes('credentials') || msg.toLowerCase().includes('password')
        ? 'Incorrect password. Please try again.'
        : msg || 'Failed to regenerate API key.');
    } finally {
      setSaving(false);
    }
  }

  function handleCopy() {
    navigator.clipboard.writeText(newApiKey).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <Dialog open={!!tenant} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, fontSize: 15, pb: 0.5, pr: 6 }}>
        Regenerate API Key
        <IconButton size="small" onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: '12px !important' }}>
        {newApiKey ? (
          <Box sx={{ textAlign: 'center', py: 1 }}>
            <Typography sx={{ fontSize: 36, mb: 1 }}>🔑</Typography>
            <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#16a34a', mb: 0.5 }}>
              API Key Regenerated
            </Typography>
            <Typography sx={{ fontSize: 13, color: brand.inkSoft, mb: 1.5 }}>
              New key for <strong>{tenant.name}</strong>. Copy it now — it won't be shown again.
            </Typography>
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              bgcolor: '#f1f5f9', border: '1px solid', borderColor: 'divider',
              borderRadius: 1, px: 1.5, py: 1, mb: 2,
            }}>
              <Typography sx={{ fontSize: 12, fontFamily: 'monospace', flex: 1, wordBreak: 'break-all' }}>
                {newApiKey}
              </Typography>
              <Tooltip title={copied ? 'Copied!' : 'Copy'}>
                <IconButton size="small" onClick={handleCopy} sx={{ color: copied ? '#16a34a' : 'inherit' }}>
                  <ContentCopyIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
            <Button variant="contained" onClick={onClose}>Done</Button>
          </Box>
        ) : (
          <Stack spacing={2}>
            {/* Tenant pill */}
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              bgcolor: '#f8fafc', borderRadius: 1, px: 1.5, py: 1,
              border: '1px solid', borderColor: 'divider',
            }}>
              <TenantTypeChip type={tenant.tenantType} />
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: brand.navy, lineHeight: 1.2 }}>
                  {tenant.name}
                </Typography>
                <Typography sx={{ fontSize: 11, color: brand.inkSoft }}>
                  {tenant.phone ?? tenant.email ?? tenant.code}
                </Typography>
              </Box>
            </Box>

            <Alert severity="warning" sx={{ py: 0.5, fontSize: 13 }}>
              This will invalidate the existing API key immediately. The tenant's integrations will stop working until they update to the new key.
            </Alert>

            <TextField
              label="Your Password"
              type={showPwd ? 'text' : 'password'}
              value={password}
              onChange={e => { setPassword(e.target.value); setError(''); }}
              fullWidth size="small"
              placeholder="Enter your super-admin password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowPwd(v => !v)} edge="end">
                      {showPwd ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {error && (
              <Alert severity="error" sx={{ py: 0.5, fontSize: 13 }}>{error}</Alert>
            )}

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', pt: 0.5 }}>
              <Button variant="outlined" onClick={onClose} disabled={saving}>Cancel</Button>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <VpnKeyIcon fontSize="small" />}
                onClick={handleRegenerate}
                disabled={saving || !password}
                sx={{ bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' } }}
              >
                {saving ? 'Regenerating…' : 'Regenerate Key'}
              </Button>
            </Box>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Tenant Detail Dialog
// ---------------------------------------------------------------------------

function TenantDetailDialog({
  tenant,
  onClose,
  onVendorChanged,
  softwareVendors,
  onChangePassword,
  onRegenerateApiKey,
}: {
  tenant: AdminTenant | null;
  onClose: () => void;
  onVendorChanged: (id: number, softwareVendorId: number | null) => void;
  softwareVendors: SoftwareVendor[];
  onChangePassword: () => void;
  onRegenerateApiKey: () => void;
}) {
  const [editingVendor, setEditingVendor] = useState(false);
  const [vendorValue, setVendorValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const [settings, setSettings] = useState<TenantSettings | null>(null);
  const [editingCredit, setEditingCredit] = useState(false);
  const [creditInput, setCreditInput] = useState('');
  const [savingCredit, setSavingCredit] = useState(false);
  const [saveCreditError, setSaveCreditError] = useState('');

  useEffect(() => {
    if (tenant) setVendorValue(String(tenant.softwareVendorId ?? ''));
  }, [tenant]);

  useEffect(() => {
    if (tenant?.tenantType === 'WHOLESALER') {
      setSettings(null);
      setEditingCredit(false);
      adminApi.getTenantSettings(tenant.id).then(s => {
        setSettings(s);
        setCreditInput(String(s.creditPeriodDays || 30));
      }).catch(() => {});
    }
  }, [tenant]);

  if (!tenant) return null;

  const row = (label: string, val?: string | null) =>
    val ? (
      <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
        <Typography sx={{ fontSize: 12, color: brand.inkSoft, minWidth: 130 }}>{label}</Typography>
        <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{val}</Typography>
      </Box>
    ) : null;

  const handleSaveVendor = async () => {
    setSaving(true); setSaveError('');
    try {
      const svId = vendorValue === '' ? null : Number(vendorValue);
      await adminApi.updateVendor(tenant.id, svId);
      onVendorChanged(tenant.id, svId);
      setEditingVendor(false);
    } catch (err) {
      setSaveError((err as { message?: string })?.message ?? 'Failed to update vendor');
    } finally {
      setSaving(false);
    }
  };

  const addressLine = [tenant.address, tenant.city, tenant.district, tenant.state, tenant.pincode]
    .filter(Boolean).join(', ');

  return (
    <Dialog open={!!tenant} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, fontSize: 15, pb: 0.5, pr: 6 }}>
        {tenant.name}
        <IconButton size="small" onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: '8px !important' }}>
        <Stack spacing={1.5}>
          {/* Identity */}
          <Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.75 }}>
              <TenantTypeChip type={tenant.tenantType} />
              <StatusChip status={tenant.status} />
              <Typography sx={{ fontSize: 11, color: brand.inkSoft }}>
                {tenant.code}
              </Typography>
            </Box>
            {row('Industry', industryLabel(tenant.industry))}
            {row('GST Number', tenant.gstin)}
          </Box>

          <Divider />

          {/* Licences */}
          <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: brand.inkSoft, mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Licences
            </Typography>
            {row('DL Number', tenant.drugLicenseNumber)}
            {row('DL Expiry', tenant.drugLicenseExpiry)}
            {tenant.hasDlDocument && (
              <Typography sx={{ fontSize: 12, color: '#16a34a' }}>✓ DL certificate uploaded</Typography>
            )}
            {row('Food License', tenant.foodLicenseNumber)}
            {row('Food Expiry', tenant.foodLicenseExpiry)}
            {tenant.hasFoodDocument && (
              <Typography sx={{ fontSize: 12, color: '#16a34a' }}>✓ Food certificate uploaded</Typography>
            )}
            {!tenant.drugLicenseNumber && !tenant.foodLicenseNumber && (
              <Typography sx={{ fontSize: 12, color: '#f59e0b' }}>⚠ No licence details on file</Typography>
            )}
          </Box>

          <Divider />

          {/* Contact & Address */}
          <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: brand.inkSoft, mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Contact
            </Typography>
            {row('Phone', tenant.phone)}
            {row('Email', tenant.email)}
            {addressLine && row('Address', addressLine)}
          </Box>

          <Divider />

          {/* Vendor */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: brand.inkSoft, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Software / ERP
              </Typography>
              {!editingVendor && (
                <IconButton size="small" onClick={() => setEditingVendor(true)}>
                  <EditIcon sx={{ fontSize: 14 }} />
                </IconButton>
              )}
            </Box>
            {editingVendor ? (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <Select value={vendorValue} onChange={e => setVendorValue(e.target.value)}>
                    <MenuItem value=""><em>None</em></MenuItem>
                    {softwareVendors.map(sv => (
                      <MenuItem key={sv.id} value={String(sv.id)}>
                        {sv.companyName} ({sv.softwareName})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button size="small" variant="contained" onClick={handleSaveVendor} disabled={saving}>
                  {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
                </Button>
                <Button size="small" onClick={() => { setEditingVendor(false); setSaveError(''); }}>
                  Cancel
                </Button>
                {saveError && <Typography sx={{ fontSize: 12, color: '#dc2626' }}>{saveError}</Typography>}
              </Box>
            ) : (
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                {swVendorLabel(tenant.softwareVendorId, softwareVendors)}
              </Typography>
            )}
          </Box>

          {row('Registered', tenant.createdAt
            ? new Date(tenant.createdAt).toLocaleDateString('en-IN')
            : undefined)}

          {tenant.tenantType === 'WHOLESALER' && (
            <>
              <Divider />
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: brand.inkSoft, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Credit Period
                  </Typography>
                  {!editingCredit && settings && (
                    <IconButton size="small" onClick={() => { setEditingCredit(true); setSaveCreditError(''); }}>
                      <EditIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  )}
                </Box>
                {editingCredit ? (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      type="number"
                      value={creditInput}
                      onChange={e => setCreditInput(e.target.value)}
                      inputProps={{ min: 1, max: 365 }}
                      sx={{ width: 100 }}
                      InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }}
                    />
                    <Button size="small" variant="contained" disabled={savingCredit} onClick={async () => {
                      const days = parseInt(creditInput, 10);
                      if (!days || days < 1) { setSaveCreditError('Must be ≥ 1'); return; }
                      setSavingCredit(true); setSaveCreditError('');
                      try {
                        const updated = await adminApi.updateTenantSettings(tenant.id, { ...settings!, creditPeriodDays: days });
                        setSettings(updated);
                        setEditingCredit(false);
                      } catch (err) {
                        setSaveCreditError((err as { message?: string })?.message ?? 'Failed to save');
                      } finally {
                        setSavingCredit(false);
                      }
                    }}>
                      {savingCredit ? <CircularProgress size={14} color="inherit" /> : 'Save'}
                    </Button>
                    <Button size="small" onClick={() => { setEditingCredit(false); setSaveCreditError(''); setCreditInput(String(settings?.creditPeriodDays ?? 30)); }}>
                      Cancel
                    </Button>
                    {saveCreditError && <Typography sx={{ fontSize: 12, color: '#dc2626' }}>{saveCreditError}</Typography>}
                  </Box>
                ) : (
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                    {settings ? `${settings.creditPeriodDays || 30} days` : '—'}
                  </Typography>
                )}
              </Box>
            </>
          )}

          <Divider />

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<LockResetIcon fontSize="small" />}
              onClick={onChangePassword}
              sx={{
                borderColor: '#f59e0b', color: '#92400e',
                '&:hover': { bgcolor: '#fffbeb', borderColor: '#d97706' },
                fontWeight: 700, fontSize: 12,
              }}
            >
              Change Password
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<VpnKeyIcon fontSize="small" />}
              onClick={onRegenerateApiKey}
              sx={{
                borderColor: '#7c3aed', color: '#6d28d9',
                '&:hover': { bgcolor: '#f5f3ff', borderColor: '#6d28d9' },
                fontWeight: 700, fontSize: 12,
              }}
            >
              Regenerate API Key
            </Button>
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Sortable header cell
// ---------------------------------------------------------------------------

function SortCell({
  col, label, active, dir, onSort, sx,
}: {
  col: SortCol;
  label: string;
  active: boolean;
  dir: SortDir;
  onSort: (col: SortCol) => void;
  sx?: object;
}) {
  return (
    <TableCell sx={{ fontWeight: 700, ...sx }}>
      <TableSortLabel
        active={active}
        direction={active ? dir : 'asc'}
        onClick={() => onSort(col)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const TYPE_TABS = [
  { value: '', label: 'All' },
  { value: 'RETAILER', label: 'Retailers' },
  { value: 'WHOLESALER', label: 'Suppliers' },
  { value: 'REPRESENTATIVE', label: 'Representatives' },
];

export function AdminTenantsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const typeParam = searchParams.get('type') ?? '';

  // Filters
  const [activeType, setActiveType] = useState(typeParam);

  // Search box, industry, and SW vendor are all "staged" — none of them hit
  // the backend until Search is clicked (or Enter is pressed); they're then
  // combined into a single backend call. noGstin/noLicense/licenseExpired
  // remain immediate-trigger chips, unaffected by this.
  const [searchInput, setSearchInput] = useState('');
  const [searchField, setSearchField] = useState<AdminTenantSearchField>('name');
  const [industryFilter, setIndustryFilter] = useState('');
  const [swVendorFilter, setSwVendorFilter] = useState<number | ''>('');

  const [appliedQuery, setAppliedQuery] = useState('');
  const [appliedSearchField, setAppliedSearchField] = useState<AdminTenantSearchField>('name');
  const [appliedIndustry, setAppliedIndustry] = useState('');
  const [appliedVendor, setAppliedVendor] = useState<number | ''>('');

  const [noGstin, setNoGstin] = useState(false);
  const [noLicense, setNoLicense] = useState(false);
  const [licenseExpired, setLicenseExpired] = useState(false);
  const [page, setPage] = useState(0);
  const [size] = useState(50);

  const handleSearch = () => {
    setAppliedQuery(searchInput.trim());
    setAppliedSearchField(searchField);
    setAppliedIndustry(industryFilter);
    setAppliedVendor(swVendorFilter);
    setPage(0);
  };

  // Sort
  const [sortCol, setSortCol] = useState<SortCol>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // Software vendors (for assignment dropdown)
  const [softwareVendors, setSoftwareVendors] = useState<SoftwareVendor[]>([]);
  useEffect(() => {
    // Full list for the filter dropdown, not the paginated Vendors table view.
    adminApi.listSoftwareVendors({ size: 1000 }).then(res => setSoftwareVendors(res.content)).catch(() => {});
  }, []);

  // Data
  const [tenants, setTenants] = useState<AdminTenant[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Detail dialog
  const [selected, setSelected] = useState<AdminTenant | null>(null);

  // Change password dialog
  const [changePwdTarget, setChangePwdTarget] = useState<AdminTenant | null>(null);

  // View / Regenerate API key dialogs
  const [viewApiKeyTarget, setViewApiKeyTarget] = useState<AdminTenant | null>(null);
  const [regenApiKeyTarget, setRegenApiKeyTarget] = useState<AdminTenant | null>(null);

  // Inline vendor edit (WHOLESALER rows only)
  const [editingVendorId,    setEditingVendorId]    = useState<number | null>(null);
  const [editingVendorValue, setEditingVendorValue] = useState('');
  const [savingVendorId,     setSavingVendorId]     = useState<number | null>(null);

  useEffect(() => {
    setActiveType(searchParams.get('type') ?? '');
    setPage(0);
  }, [searchParams]);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const result = await adminApi.listTenants({
        type: activeType || undefined,
        q: appliedQuery || undefined,
        searchField: appliedSearchField,
        noGstin,
        noLicense,
        licenseExpired,
        softwareVendorId: appliedVendor || undefined,
        industry: appliedIndustry || undefined,
        page,
        size,
      });
      setTenants(result.content);
      setTotal(result.totalElements);
    } catch (err) {
      setError((err as { message?: string })?.message ?? 'Failed to load tenants');
    } finally {
      setLoading(false);
    }
  }, [activeType, appliedQuery, appliedSearchField, noGstin, noLicense, licenseExpired, appliedVendor, appliedIndustry, page, size]);

  useEffect(() => { load(); }, [load]);

  const handleTypeTab = (value: string) => {
    setActiveType(value);
    setPage(0);
    setSearchParams(value ? { type: value } : {});
  };

  const toggleChip = (current: boolean, setter: (v: boolean) => void) => {
    setter(!current); setPage(0);
  };

  const handleSort = (col: SortCol) => {
    if (col === sortCol) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  const handleVendorChanged = (id: number, softwareVendorId: number | null) => {
    setTenants(prev => prev.map(t =>
      t.id === id ? { ...t, softwareVendorId: softwareVendorId ?? undefined } : t
    ));
    if (selected?.id === id) {
      setSelected(prev => prev ? { ...prev, softwareVendorId: softwareVendorId ?? undefined } : null);
    }
  };

  const handleInlineVendorSave = async (id: number) => {
    setSavingVendorId(id);
    try {
      const svId = editingVendorValue === '' ? null : Number(editingVendorValue);
      await adminApi.updateVendor(id, svId);
      handleVendorChanged(id, svId);
      setEditingVendorId(null);
    } finally { setSavingVendorId(null); }
  };

  const isLicenseExpiredRow = (t: AdminTenant) => {
    const today = new Date().toISOString().slice(0, 10);
    return (t.drugLicenseExpiry && t.drugLicenseExpiry < today) ||
           (t.foodLicenseExpiry && t.foodLicenseExpiry < today);
  };

  const displayed = sortedTenants(tenants, sortCol, sortDir);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      <PageHeader
        title="Registered Users"
        subtitle={`${total} business${total !== 1 ? 'es' : ''} registered`}
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/admin/tenants/new')}
            size="small"
          >
            Create User
          </Button>
        }
      />

      {/* Type tabs */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
        {TYPE_TABS.map(tab => (
          <Chip
            key={tab.value}
            label={tab.label}
            onClick={() => handleTypeTab(tab.value)}
            variant={activeType === tab.value ? 'filled' : 'outlined'}
            color={activeType === tab.value ? 'primary' : 'default'}
            size="small"
            sx={{ fontWeight: 600 }}
          />
        ))}
      </Box>

      {/* Search + filter row */}
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
        <AdminSearchWithFieldSelect
          field={searchField}
          onFieldChange={v => setSearchField(v as AdminTenantSearchField)}
          options={TENANT_SEARCH_FIELD_OPTIONS}
          value={searchInput}
          onChangeText={setSearchInput}
          onSearch={handleSearch}
        />

        {/* Industry filter — staged, applied only when Search is pressed */}
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Industry</InputLabel>
          <Select
            value={industryFilter}
            label="Industry"
            onChange={e => setIndustryFilter(e.target.value)}
          >
            <MenuItem value="">All Industries</MenuItem>
            {INDUSTRY_OPTIONS.map(o => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* SW Vendor filter — staged, applied only when Search is pressed */}
        <FormControl size="small" sx={{ minWidth: 175 }}>
          <InputLabel>SW Vendor</InputLabel>
          <Select
            value={swVendorFilter}
            label="SW Vendor"
            onChange={e => setSwVendorFilter(e.target.value as number | '')}
          >
            <MenuItem value="">All</MenuItem>
            {softwareVendors.map(sv => (
              <MenuItem key={sv.id} value={sv.id}>
                <Box>
                  <Typography sx={{ fontSize: 13, lineHeight: 1.2 }}>{sv.softwareName}</Typography>
                  <Typography sx={{ fontSize: 11, color: 'text.secondary', lineHeight: 1.2 }}>{sv.companyName}</Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Chip
          label="No GST"
          onClick={() => toggleChip(noGstin, setNoGstin)}
          variant={noGstin ? 'filled' : 'outlined'}
          color={noGstin ? 'warning' : 'default'}
          size="small"
          sx={{ fontWeight: 600 }}
        />
        <Chip
          label="No License"
          onClick={() => toggleChip(noLicense, setNoLicense)}
          variant={noLicense ? 'filled' : 'outlined'}
          color={noLicense ? 'warning' : 'default'}
          size="small"
          sx={{ fontWeight: 600 }}
        />
        <Chip
          label="License Expired"
          onClick={() => toggleChip(licenseExpired, setLicenseExpired)}
          variant={licenseExpired ? 'filled' : 'outlined'}
          color={licenseExpired ? 'error' : 'default'}
          size="small"
          sx={{ fontWeight: 600 }}
        />

        <Tooltip title="Refresh">
          <IconButton size="small" onClick={load} disabled={loading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && tenants.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table size="small" sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow sx={{ background: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 700, width: 40 }}>#</TableCell>

                  <SortCell col="name"       label="Business Name"  active={sortCol === 'name'}       dir={sortDir} onSort={handleSort} />
                  <SortCell col="tenantType" label="Type"           active={sortCol === 'tenantType'} dir={sortDir} onSort={handleSort} sx={{ width: 110 }} />
                  <SortCell col="industry"   label="Industry"       active={sortCol === 'industry'}   dir={sortDir} onSort={handleSort} sx={{ width: 140 }} />

                  {/* GST + DL combined */}
                  <TableCell sx={{ fontWeight: 700 }}>GST / DL Number</TableCell>

                  <SortCell col="phone"  label="Phone"  active={sortCol === 'phone'}  dir={sortDir} onSort={handleSort} sx={{ width: 120 }} />
                  <TableCell sx={{ fontWeight: 700, width: 150 }}>SW Vendor</TableCell>
                  <SortCell col="status" label="Status" active={sortCol === 'status'} dir={sortDir} onSort={handleSort} sx={{ width: 90 }} />
                  <TableCell sx={{ fontWeight: 700, width: 80, px: 0.5 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {displayed.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 6, color: brand.inkSoft }}>
                      No vendors match the selected filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  displayed.map((t, idx) => {
                    const expired = isLicenseExpiredRow(t);
                    const locationLine = [t.city, t.state].filter(Boolean).join(', ');
                    return (
                      <Tooltip
                        key={t.id}
                        placement="right"
                        arrow
                        title={
                          <Box sx={{ p: 0.5, minWidth: 220 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 0.5 }}>{t.name}</Typography>
                            {t.industry && <Typography sx={{ fontSize: 12 }}>Industry: {industryLabel(t.industry)}</Typography>}
                            {locationLine && <Typography sx={{ fontSize: 12 }}>📍 {locationLine}</Typography>}
                            {t.address && <Typography sx={{ fontSize: 12 }}>{t.address}</Typography>}
                            {t.email && <Typography sx={{ fontSize: 12 }}>📧 {t.email}</Typography>}
                            {t.gstin && <Typography sx={{ fontSize: 12 }}>GST: {t.gstin}</Typography>}
                            {t.drugLicenseNumber && <Typography sx={{ fontSize: 12 }}>DL: {t.drugLicenseNumber}</Typography>}
                            <Typography sx={{ fontSize: 12 }}>Software: {swVendorLabel(t.softwareVendorId, softwareVendors)}</Typography>
                            <Typography sx={{ fontSize: 11, color: '#a0aec0', mt: 0.5 }}>
                              Click for full details
                            </Typography>
                          </Box>
                        }
                      >
                        <TableRow
                          hover
                          sx={{
                            cursor: 'pointer',
                            '&:nth-of-type(even)': { background: '#fbfbfd' },
                            ...(expired ? { background: '#fff7ed !important' } : {}),
                          }}
                          onClick={() => setSelected(t)}
                        >
                          <TableCell sx={{ color: brand.inkSoft, fontSize: 12 }}>
                            {page * size + idx + 1}
                          </TableCell>

                          {/* Business Name + location */}
                          <TableCell>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: brand.navy, lineHeight: 1.3 }}>
                              {t.name}
                            </Typography>
                            {locationLine && (
                              <Typography sx={{ fontSize: 11, color: brand.inkSoft, lineHeight: 1.2 }}>
                                📍 {locationLine}
                              </Typography>
                            )}
                            <Typography sx={{ fontSize: 10, color: '#b0b8c8' }}>{t.code}</Typography>
                          </TableCell>

                          <TableCell><TenantTypeChip type={t.tenantType} /></TableCell>

                          {/* Industry */}
                          <TableCell sx={{ fontSize: 12, color: brand.inkSoft }}>
                            {industryLabel(t.industry)}
                          </TableCell>

                          {/* GST + DL combined column */}
                          <TableCell>
                            {t.gstin ? (
                              <Typography sx={{ fontSize: 12 }}>
                                <Typography component="span" sx={{ fontSize: 10, color: brand.inkSoft, mr: 0.5 }}>GST</Typography>
                                {t.gstin}
                              </Typography>
                            ) : (
                              <Typography sx={{ fontSize: 12, color: '#9ca3af' }}>—</Typography>
                            )}
                            {t.drugLicenseNumber && (
                              <Typography sx={{ fontSize: 12, color: expired ? '#dc2626' : 'inherit', lineHeight: 1.3 }}>
                                <Typography component="span" sx={{ fontSize: 10, color: expired ? '#dc2626' : brand.inkSoft, mr: 0.5 }}>DL</Typography>
                                {t.drugLicenseNumber}
                                {expired && ' ⚠'}
                              </Typography>
                            )}
                          </TableCell>

                          <TableCell sx={{ fontSize: 12 }}>{t.phone ?? '—'}</TableCell>
                          {/* Software — inline edit for all types */}
                          <TableCell onClick={e => e.stopPropagation()}>
                            {editingVendorId === t.id ? (
                              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                <FormControl size="small" sx={{ minWidth: 160 }}>
                                  <Select value={editingVendorValue}
                                    onChange={e => setEditingVendorValue(e.target.value)}
                                    sx={{ fontSize: 12 }}>
                                    <MenuItem value="" sx={{ fontSize: 12 }}><em>None</em></MenuItem>
                                    {softwareVendors.map(sv => (
                                      <MenuItem key={sv.id} value={String(sv.id)} sx={{ fontSize: 12 }}>
                                        {sv.companyName} ({sv.softwareName})
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                <IconButton size="small" color="primary"
                                  onClick={() => handleInlineVendorSave(t.id)}
                                  disabled={savingVendorId === t.id}>
                                  {savingVendorId === t.id
                                    ? <CircularProgress size={13} />
                                    : <EditIcon sx={{ fontSize: 14 }} />}
                                </IconButton>
                                <IconButton size="small" onClick={() => setEditingVendorId(null)}>
                                  <CloseIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Box>
                            ) : (() => {
                              const sv = softwareVendors.find(v => v.id === t.softwareVendorId);
                              return (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Box>
                                    {sv ? (
                                      <>
                                        <Typography sx={{ fontSize: 12, fontWeight: 600, lineHeight: 1.3 }}>
                                          {sv.softwareName}
                                        </Typography>
                                        <Typography sx={{ fontSize: 11, color: brand.inkSoft, lineHeight: 1.2 }}>
                                          {sv.companyName}
                                        </Typography>
                                      </>
                                    ) : (
                                      <Typography sx={{ fontSize: 12, color: '#9ca3af' }}>—</Typography>
                                    )}
                                  </Box>
                                  <Tooltip title="Assign software">
                                    <IconButton size="small" sx={{ p: 0.25, opacity: 0.4, '&:hover': { opacity: 1 } }}
                                      onClick={() => { setEditingVendorId(t.id); setEditingVendorValue(String(t.softwareVendorId ?? '')); }}>
                                      <EditIcon sx={{ fontSize: 12 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              );
                            })()}
                          </TableCell>
                          <TableCell><StatusChip status={t.status} /></TableCell>

                          {/* Row actions — stop propagation so row click doesn't open detail */}
                          <TableCell onClick={e => e.stopPropagation()} sx={{ width: 112, px: 0.5 }}>
                            <Box sx={{ display: 'flex', gap: 0.25 }}>
                              <Tooltip title="Change password">
                                <IconButton
                                  size="small"
                                  onClick={() => setChangePwdTarget(t)}
                                  sx={{ color: '#d97706', '&:hover': { bgcolor: '#fffbeb' } }}
                                >
                                  <LockResetIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="View API key">
                                <IconButton
                                  size="small"
                                  onClick={() => setViewApiKeyTarget(t)}
                                  sx={{ color: '#0369a1', '&:hover': { bgcolor: '#eff6ff' } }}
                                >
                                  <VisibilityIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Regenerate API key">
                                <IconButton
                                  size="small"
                                  onClick={() => setRegenApiKeyTarget(t)}
                                  sx={{ color: '#7c3aed', '&:hover': { bgcolor: '#f5f3ff' } }}
                                >
                                  <VpnKeyIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      </Tooltip>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <PaginationFooter
            page={page}
            totalPages={Math.max(1, Math.ceil(total / size))}
            total={total}
            rowsPerPage={size}
            rowsPerPageOptions={[size]}
            onPageChange={setPage}
            onRowsPerPageChange={() => {}}
            label="tenants"
          />
        </>
      )}

      {/* Tenant detail dialog */}
      <TenantDetailDialog
        tenant={selected}
        onClose={() => setSelected(null)}
        onVendorChanged={handleVendorChanged}
        softwareVendors={softwareVendors}
        onChangePassword={() => {
          if (selected) {
            setChangePwdTarget(selected);
            setSelected(null);
          }
        }}
        onRegenerateApiKey={() => {
          if (selected) {
            setRegenApiKeyTarget(selected);
            setSelected(null);
          }
        }}
      />

      {/* Change password dialog */}
      <ChangePasswordDialog
        tenant={changePwdTarget}
        onClose={() => setChangePwdTarget(null)}
      />

      {/* View API key dialog */}
      <ViewApiKeyDialog
        tenant={viewApiKeyTarget}
        onClose={() => setViewApiKeyTarget(null)}
      />

      {/* Regenerate API key dialog */}
      <RegenerateApiKeyDialog
        tenant={regenApiKeyTarget}
        onClose={() => setRegenApiKeyTarget(null)}
      />
    </Box>
  );
}
